/**
 * Small controlled popover anchored to a DOM element (a table cell or button).
 * Surface is Vibe's DialogContentContainer; positioning is manual so it works
 * against arbitrary anchors inside the scrolling board table.
 * Closes on outside mousedown and Escape.
 *
 * Rendered into <body> through a portal. The anchors often sit in the table's
 * sticky first column (`position: sticky; z-index: 3`), which makes a stacking
 * context: a popover rendered inside it stays under the next rows' sticky cells
 * and the header whatever its own z-index. From <body> it stacks above all of
 * them. The monday theme class is on <body> (lib/theme), so it keeps its colours.
 *
 * Vibe opens its menus (a Dropdown's list, the calendar's month and year) in
 * layers of their own, also portalled to <body>. Opened from inside the
 * popover, they belong to it: a click in one is not an outside click, Escape
 * closes the menu before the popover, and AnchoredPopover.css lifts them above it.
 */
import { DialogContentContainer } from '@vibe/core';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './AnchoredPopover.css';

/** Vibe's floating layer: every Dropdown menu and the calendar's month/year menus. */
const VIBE_LAYER = '.monday-style-dialog-content-wrapper';
const inVibeLayer = (el) => el instanceof Element && Boolean(el.closest(VIBE_LAYER));
/** Is `el` a Vibe control whose menu is open in a layer? (A box-mode list sits inline.) */
const opensLayerMenu = (el) => {
  const owner = el instanceof Element && el.closest('[aria-expanded="true"][aria-controls]');
  const list = owner && document.getElementById(owner.getAttribute('aria-controls'));
  return inVibeLayer(list);
};

export default function AnchoredPopover({ anchorEl, onClose, width = 260, children }) {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Placed below the anchor, or above it when there is no room below; never
  // taller than the window (it scrolls inside instead). Placed again whenever
  // its size changes: a cell editor grows after opening (the date editor's
  // Dynamic tab), and a popover placed for its first size ran off the bottom.
  const place = useCallback(() => {
    if (!anchorEl || !ref.current) return;
    const r = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxHeight = Math.max(120, vh - 16);
    // natural height = the content's full height plus the dialog's own padding
    const scroller = scrollRef.current;
    const chrome = scroller ? ref.current.offsetHeight - scroller.clientHeight : 0;
    const natural = scroller ? scroller.scrollHeight + chrome : ref.current.offsetHeight;
    const h = Math.min(natural, maxHeight);
    const left = Math.max(8, Math.min(r.left, vw - width - 12));
    let top = r.bottom + 4;
    if (top + h > vh - 8) {
      top = r.top - 4 - h >= 8 ? r.top - 4 - h : Math.max(8, vh - h - 8);
    }
    const innerMax = Math.max(80, maxHeight - chrome);
    setPos((prev) => (prev && prev.left === left && prev.top === top && prev.innerMax === innerMax ? prev : { left, top, innerMax }));
  }, [anchorEl, width]);

  useLayoutEffect(() => {
    place();
  }, [place, children]);

  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => place());
    ro.observe(ref.current);
    window.addEventListener('resize', place);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', place);
    };
  }, [place]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target) && e.target !== anchorEl && !inVibeLayer(e.target)) onClose();
    };
    // on capture: by the bubble phase an open menu has already closed itself
    const onKey = (e) => {
      if (e.key === 'Escape' && !opensLayerMenu(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return ReactDOM.createPortal(
    <div
      ref={ref}
      className="spt-anchored-popover"
      style={{
        position: 'fixed',
        zIndex: 1000,
        width,
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
      }}
    >
      {/* scrolls inside the dialog, so its shadow and corners are never clipped */}
      <DialogContentContainer className="spt-popover">
        <div ref={scrollRef} className="spt-popover-scroll" style={{ maxHeight: pos ? pos.innerMax : undefined }}>
          {children}
        </div>
      </DialogContentContainer>
    </div>,
    document.body,
  );
}
